import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { AuthService, classifyRefreshFailure } from "@modules/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

@Injectable()
export class AuthGuard {
  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const extras = this.router.getCurrentNavigation()?.extras;
    if (extras?.state?.ignoreAuth) return of(true);

    if (this.authService.isAuthenticated()) return of(true);

    if (!this.authService.getRefreshToken())
      return of(this.router.createUrlTree(["/"]));

    return this.authService.refreshToken().pipe(
      map(() =>
        this.authService.getAccessToken()
          ? true
          : this.router.createUrlTree(["/"])
      ),
      catchError((error: unknown) => {
        // Only send the user back to the start when the auth server rejected
        // the grant. If the refresh merely could not reach the server, the
        // refresh token is still usable, so allow the navigation and let the
        // auth interceptor retry rather than dropping the user out of the app.
        const canRecover =
          classifyRefreshFailure(error) !== "session-ended" &&
          !!this.authService.getRefreshToken();

        if (canRecover) return of(true);

        return of(this.router.createUrlTree(["/"]));
      })
    );
  }
}
